'use client'

import { Ellipsis } from 'lucide-react'
import { memo, useCallback, useMemo, useState } from 'react'

import { ActionButton } from './components/ActionButton'
import { ConfirmationDialog } from './components/ConfirmationDialog'
import { CustomActionRenderer } from './components/CustomActionRenderer'
import { FormDialog } from './components/FormDialog'
import { useActionExecution } from './hooks/useActionExecution'
import { useActionParams, type ResponseData } from './hooks/useActionParams'
import { useActionsFilter } from './hooks/useActionsFilter'
import { useCustomActions } from './hooks/useCustomActions'
import type { DynamicActionsProps, Action } from './types'
import { ExternalIcon } from '@components/Layout/NavLink'
import { Button, Separator } from '@fp/ui-kit'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@ui/DropDownMenu'

/*
  DynamicActions:
  - render actions from backend
  
  Example Simple:
    <DynamicActions
      type='enrollment_requests'
      itemId={item.id}
      actions={item.actions}
      actionFilter='show_all'
      onSuccess={(res) => console.log(`onSuccess action`, res)}
    />

  Example with custom actions:
    <DynamicActions
      type='user'
      itemId={user?.id}
      actions={user?.actions || []}
      onSuccess={onSuccessUpdateData}
      actionFilter={{
        visible: ['edit', 'reset_password', 'delete_user'],
      }}
      customActions={{
        edit: { // key of the custom action ('edit' could be used in "props.actionFilter")
          component: EditUserSheet, // Custom component to render
          props: { // Props to pass to the custom component
            userId: user?.id,
            onSuccess: onSuccessUpdateData,
          },
          // isDropdownHidden: true, // if true, button hidden in dropdown
          button: { // Button for the custom action
            label: 'Edit',
            icon: <Pencil />,
            variant: 'default',
            size: 'sm',
          },
        },
        ... // other custom actions
      }}
    />
*/
export const DynamicActions = memo<DynamicActionsProps>(
  ({
    actions,
    itemId,
    onSuccess,
    customActions = {},
    actionFilter = 'show_all',
    className = '',
    type,
    dropdownItems,
  }) => {
    const [confirmationIsOpen, setConfirmationIsOpen] = useState(false)
    const [formIsOpen, setFormIsOpen] = useState(false)
    const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
    const [currentAction, setCurrentAction] = useState<Action>()

    // Filter actions based on configuration
    const { visibleActions, hiddenActions } = useActionsFilter(actions, actionFilter)

    // Get action parameters for forms
    const { actionParamsMap, fetchActionParams } = useActionParams(itemId, type)

    // Handle action execution
    // The executed action comes from the hook, not `currentAction` state, which is still stale on the first click.
    const { executeAction } = useActionExecution({ onSuccess })

    const handleActionClick = useCallback(
      (action: Action) => async () => {
        setCurrentAction(action)
        fetchActionParams(action)

        if (action.confirm) {
          // Confirmation dialog
          setConfirmationIsOpen(true)
          setFormIsOpen(false)
          setDropdownIsOpen(false)
        } else if (action.require_params) {
          // Form dialog
          setFormIsOpen(true)
          setConfirmationIsOpen(false)
          setDropdownIsOpen(false)
        } else {
          // Instant Execute Action
          await executeAction(action)
          setDropdownIsOpen(false)
        }
      },
      [executeAction, fetchActionParams],
    )

    const handleDropdownItemClick = useCallback(
      (action: Action) => () => handleActionClick(action)(),
      [handleActionClick],
    )

    const currentActionParams = useMemo(
      () => (currentAction?.action_name ? actionParamsMap[currentAction.action_name] : undefined),
      [currentAction, actionParamsMap],
    )

    // Handle custom actions with dialog state management
    const { visibleCustomActions, hiddenCustomActions, openDialog, CustomDialogs } = useCustomActions(
      customActions,
      itemId,
      (res?: ResponseData) => onSuccess?.(res, undefined),
    )

    const onActionClick = useCallback(
      (dialogKey: string) => {
        setDropdownIsOpen(false)
        openDialog(dialogKey)
      },
      [openDialog, setDropdownIsOpen],
    )

    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {/* Visible custom actions */}
        <div className='flex flex-wrap items-center gap-2'>
          {Object.keys(visibleCustomActions).length > 0 && (
            <CustomActionRenderer customActions={visibleCustomActions} accountId={itemId} onActionClick={openDialog} />
          )}

          {/* Visible action buttons */}
          {visibleActions.length > 0 &&
            visibleActions.map((action) => (
              <ActionButton key={action.action_name} action={action} onClick={handleActionClick(action)} />
            ))}
        </div>

        {/* Separator between visible and dropdown actions */}
        {visibleActions.length > 0 && hiddenActions.length > 0 && <Separator orientation='vertical' />}

        {/* Dropdown for hidden actions */}
        {(hiddenActions.length > 0 || Object.keys(hiddenCustomActions).length > 0 || dropdownItems) && (
          <DropdownMenu open={dropdownIsOpen} onOpenChange={setDropdownIsOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' size='iconCircle' aria-label='More actions'>
                <Ellipsis />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align='start' className='min-w-64'>
              {dropdownItems}

              {/* Hidden custom actions as dropdown items */}
              {
                <CustomActionRenderer
                  customActions={hiddenCustomActions}
                  accountId={itemId}
                  asDropdownItems
                  onActionClick={onActionClick}
                />
              }

              {/* Backend hidden actions */}
              {hiddenActions.map((action) => (
                <DropdownMenuItem
                  key={action.action_name}
                  onSelect={handleDropdownItemClick(action)}
                  textValue={action.name?.replaceAll(' ', '_')}
                >
                  {action.name}
                  {action.external && <ExternalIcon />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Render all open custom action dialogs outside, to avoid issue with closed DropdownMenu */}
        {CustomDialogs}

        {/* Dialogs */}
        <ConfirmationDialog
          action={currentAction}
          isOpen={confirmationIsOpen}
          setIsOpen={setConfirmationIsOpen}
          onSuccess={onSuccess}
        />

        {/* FormDialog */}
        {currentAction && (
          <FormDialog
            action={currentAction}
            params={currentActionParams}
            isOpen={formIsOpen}
            setIsOpen={setFormIsOpen}
            onSuccess={onSuccess}
            onDynamicActionSelect={handleActionClick}
          />
        )}
      </div>
    )
  },
)

DynamicActions.displayName = 'DynamicActions'
